import { Badge, Button, Drawer, DrawerBody, DrawerContent, DrawerHeader, useDisclosure } from "@heroui/react";
import { useStore } from "@nanostores/react";
import { ActivityIcon, FilePenLineIcon } from "lucide-react";
import { useRef } from "react";
import { TokenProposalStatus, type TokenDetails } from "../queries/tokenReg";

// Use TokenDetails instead of ProposedTokenData
type ProposedTokenData = TokenDetails;
import { useBridgeClaimMutation } from "../mutations/bridge";
import { useL1TokenRegMutation, useL2TokenRegMutation } from "../mutations/tokenReg";
import { useProposedTokensQuery } from "../queries/tokenReg";
import { $bridgeTxHistoryAtom } from "../state/bridgeTxHistory";
import type { BridgeTxClaimedFailed, BridgeTxClaimedSuccess, BridgeTxUnclaimedSuccess } from "../state/types";
import { ClaimFailedModal } from "./bridge/ClaimFailedModal";
import { ClaimSuccessModal } from "./bridge/ClaimSuccessModal";
import { ClaimWaitingModal } from "./bridge/ClaimWaitingModal";

export function ActivityPanelNavbarItem() {
  const { isOpen, onOpen, onOpenChange } = useDisclosure();

  const bridgeTxHistory = useStore($bridgeTxHistoryAtom);
  const unclaimedBridgeTxs: BridgeTxUnclaimedSuccess[] = bridgeTxHistory
    .filter((tx) => tx.state === "unclaimed")
    .map((tx) => ({
      ...tx,
      txStatus: "success",
    }));

  const proposedTokensQuery = useProposedTokensQuery();
  const l1UnregisteredTokens =
    proposedTokensQuery.data?.filter(
      (td) => td.l1ProposalStatus === TokenProposalStatus.ACCEPTED && !td.l1RegistrationStatus,
    ) ?? [];
  const l2UnregisteredTokens =
    proposedTokensQuery.data?.filter(
      (td) =>
        td.l1ProposalStatus === TokenProposalStatus.ACCEPTED && td.l1RegistrationStatus && !td.l2RegistrationStatus,
    ) ?? [];

  const activityCount = unclaimedBridgeTxs.length + l1UnregisteredTokens.length + l2UnregisteredTokens.length;

  return (
    <>
      <Badge color="primary" size="lg" content={activityCount} isInvisible={activityCount === 0}>
        <Button size="lg" radius="full" isIconOnly className="bg-[#245274]" onPress={onOpen}>
          <ActivityIcon />
        </Button>
      </Badge>
      <Drawer
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        classNames={{
          base: "turnstile-modal-border rounded-3xl p-2 inset-y-8 right-8",
          closeButton: "text-white text-2xl top-5 right-6",
          header: "pt-5 pb-3",
          body: "items-center p-6 gap-4",
          backdrop: "turnstile-modal-backdrop",
        }}
      >
        <DrawerContent>
          <DrawerHeader className="flex flex-col gap-2">
            <div className="text-xl">Activity</div>
            <div className="text-[#ADC0D0] text-sm font-normal">
              This is where all your notifications of actions requiring your confirmation will appear.
            </div>
          </DrawerHeader>
          <DrawerBody>
            <BridgeActivityList unclaimedBridgeTxs={unclaimedBridgeTxs} />
            <L1RegActivityList l1UnregisteredTokens={l1UnregisteredTokens} />
            <L2RegActivityList l2UnregisteredTokens={l2UnregisteredTokens} />
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
}

function BridgeActivityList({
  unclaimedBridgeTxs,
}: {
  unclaimedBridgeTxs: BridgeTxUnclaimedSuccess[];
}) {
  const unclaimedTxDataRef = useRef<BridgeTxUnclaimedSuccess | null>(null);
  const claimedSuccessTxDataRef = useRef<BridgeTxClaimedSuccess | null>(null);
  const claimedFailedTxDataRef = useRef<BridgeTxClaimedFailed | null>(null);

  const bridgeClaimMutation = useBridgeClaimMutation({
    onSuccess: (claimedTxData) => {
      claimedSuccessTxDataRef.current = claimedTxData;
      claimedFailedTxDataRef.current = null;
    },
    onError: (txData) => {
      claimedFailedTxDataRef.current = txData;
      claimedSuccessTxDataRef.current = null;
    },
  });

  return (
    <>
      {unclaimedBridgeTxs.map((tx) => (
        <div key={tx.id} className="flex flex-col gap-4 bg-[#497393] px-8 py-6 rounded-xl">
          <div className="flex gap-4 items-start">
            <div>
              <FilePenLineIcon className="size-6 text-[#FCBD58]" />
            </div>
            <div className="flex flex-col gap-2">
              <div className="font-semibold">Claim Tokens</div>
              <div className="text-[#ADC0D0] text-tiny font-normal">
                Claim your tokens on the Aztec Network to finish your Bridge transaction.
              </div>
            </div>
          </div>
          <Button
            fullWidth
            color="primary"
            radius="full"
            isLoading={bridgeClaimMutation.status === "pending"}
            onPress={() => {
              unclaimedTxDataRef.current = tx;
              bridgeClaimMutation.mutate({
                ...tx,
                isManualClaim: false,
              });
            }}
          >
            Claim
          </Button>
        </div>
      ))}

      <ClaimWaitingModal
        isOpen={bridgeClaimMutation.status === "pending"}
        unclaimedTxData={unclaimedTxDataRef.current}
      />

      <ClaimSuccessModal
        isOpen={bridgeClaimMutation.status === "success"}
        onClose={() => {
          bridgeClaimMutation.reset();
        }}
        claimedTxData={claimedSuccessTxDataRef.current}
      />

      <ClaimFailedModal
        isOpen={bridgeClaimMutation.status === "error"}
        onClose={() => {
          bridgeClaimMutation.reset();
        }}
        failedTxData={claimedFailedTxDataRef.current}
      />
    </>
  );
}

function L1RegActivityList({
  l1UnregisteredTokens,
}: {
  l1UnregisteredTokens: ProposedTokenData[];
}) {
  const l1RegMutation = useL1TokenRegMutation();

  return (
    <>
      {l1UnregisteredTokens.map((token) => (
        <div key={token.tokenAddress} className="flex flex-col gap-4 bg-[#497393] px-8 py-6 rounded-xl">
          <div className="flex gap-4 items-start">
            <div>
              <FilePenLineIcon className="size-6 text-[#FCBD58]" />
            </div>
            <div className="flex flex-col gap-2">
              <div className="font-semibold">Register token on L1</div>
              <div className="text-[#ADC0D0] text-tiny font-normal">
                Your proposed token is ready to be registered on L1, complete the process to make it available on the
                Aztec Network.
              </div>
            </div>
          </div>
          <Button
            fullWidth
            color="primary"
            radius="full"
            isLoading={l1RegMutation.status === "pending"}
            onPress={() => {
              l1RegMutation.mutate({
                tokenAddress: token.tokenAddress,
              });
            }}
          >
            Complete
          </Button>
        </div>
      ))}
    </>
  );
}

function L2RegActivityList({
  l2UnregisteredTokens,
}: {
  l2UnregisteredTokens: ProposedTokenData[];
}) {
  const l2RegMutation = useL2TokenRegMutation();

  return (
    <>
      {l2UnregisteredTokens.map((token) => (
        <div key={token.tokenAddress} className="flex flex-col gap-4 bg-[#497393] px-8 py-6 rounded-xl">
          <div className="flex gap-4 items-start">
            <div>
              <FilePenLineIcon className="size-6 text-[#FCBD58]" />
            </div>
            <div className="flex flex-col gap-2">
              <div className="font-semibold">Register token on L2</div>
              <div className="text-[#ADC0D0] text-tiny font-normal">
                Your proposed token is ready to be registered on L2, complete the process to make it available on the
                Aztec Network.
              </div>
            </div>
          </div>
          <Button
            fullWidth
            color="primary"
            radius="full"
            isLoading={l2RegMutation.status === "pending"}
            onPress={() => {
              l2RegMutation.mutate({
                tokenAddress: token.tokenAddress,
              });
            }}
          >
            Complete
          </Button>
        </div>
      ))}
    </>
  );
}
